import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { fetchEventTarget } from '@/features/events/api';
import { eventSurveyTabs } from '@/features/events/eventSurveyTabs';
import { fetchEventSurveys, type EventSurveyKind } from '@/features/surveys/api';

/**
 * イベント作成後の「次にやること」（D-099）。
 *
 * 作っただけでは受講生に見えず、対象者の設定も課題の割当も別タブにあるため、
 * 何が済んで何が残っているのかが画面から分からなかった。
 * 下書きのあいだだけ、残っている作業を上部に並べる。
 */
export function EventNextSteps({
  eventId,
  isDraft,
  isMeeting = false,
  allowPostSurvey = false,
  typeCode,
  surveyEnabled,
  onDisableKind,
  onGoToTab,
  onPublish,
  publishing,
}: {
  eventId: string;
  isDraft: boolean;
  /** 面談（サクセス面談・1on1）は出欠・席次を使わない（D-177） */
  isMeeting?: boolean;
  /** 1on1は事後アンケートだけ割り当てる（D-271） */
  allowPostSurvey?: boolean;
  /** 種別コード（月例会はタブ名・順序が異なる / D-329） */
  typeCode?: string | null;
  surveyEnabled: Record<EventSurveyKind, boolean>;
  onDisableKind: (kind: EventSurveyKind) => void;
  onGoToTab: (tabId: string) => void;
  onPublish: () => void;
  publishing: boolean;
}): ReactElement | null {
  const showSurveySteps = !isMeeting || allowPostSurvey;
  const targetQuery = useQuery({
    queryKey: ['event-target', eventId],
    queryFn: () => fetchEventTarget(eventId),
    enabled: isDraft,
  });
  const surveysQuery = useQuery({
    queryKey: ['event-surveys', eventId],
    queryFn: () => fetchEventSurveys(eventId),
    enabled: isDraft && showSurveySteps,
  });

  if (!isDraft) return null;

  const targetCount = targetQuery.data?.matched_count ?? 0;
  const hasTarget = targetCount > 0;

  const steps: {
    done: boolean;
    label: string;
    detail: string;
    tab: string;
    tabLabel: string;
    unusedKind?: EventSurveyKind | undefined;
  }[] = [
    {
      done: hasTarget,
      label: '対象者を決める',
      detail: hasTarget
        ? `${targetCount}名が対象になっています`
        : 'まだ誰も対象になっていません。このままでは公開しても誰にも見えません',
      tab: 'target',
      tabLabel: '対象者',
    },
  ];

  if (showSurveySteps) {
    const groups = allowPostSurvey
      ? eventSurveyTabs(typeCode).filter((group) => group.kind === 'post')
      : eventSurveyTabs(typeCode);
    for (const group of groups) {
      const count = surveysQuery.data?.filter((s) => s.template_kind === group.kind).length ?? 0;
      const enabled = surveyEnabled[group.kind];
      steps.push({
        done: !enabled || count > 0,
        label: `${group.label}を割り当てる（任意）`,
        detail: !enabled
          ? 'この回では使いません'
          : count > 0
            ? `${count}件を割り当て済み`
            : 'まだ割り当てられていません',
        tab: group.tabId,
        tabLabel: group.label,
        unusedKind: enabled && count === 0 ? group.kind : undefined,
      });
    }
  }

  return (
    <section className="mb-4 rounded-sm border border-wpnotice-warning bg-wpnotice-warning/10 px-4 py-3">
      <h2 className="mb-1 font-semibold text-wptext-strong">
        次にやること（このイベントはまだ下書きです）
      </h2>
      <p className="mb-3 text-wptext-muted">
        公開するまで受講生には表示されません。案内メールの予約は作成時に入っていますが、公開前は時刻が来ても送らずスキップします。
      </p>

      <ol className="mb-3 flex flex-col gap-2">
        {steps.map((step) => (
          <li key={step.label} className="flex flex-wrap items-center gap-2">
            <span
              aria-hidden="true"
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                step.done ? 'bg-wpnotice-success text-white' : 'bg-wpborder text-wptext-strong'
              }`}
            >
              {step.done ? '✓' : '!'}
            </span>
            <span className={step.done ? 'text-wptext-muted' : 'font-semibold text-wptext-strong'}>
              {step.label}
            </span>
            <span className="text-wptext-muted">— {step.detail}</span>
            <button
              type="button"
              onClick={() => onGoToTab(step.tab)}
              className="text-wpaccent hover:underline"
            >
              「{step.tabLabel}」タブへ
            </button>
            {step.unusedKind !== undefined && (
              <button
                type="button"
                onClick={() => {
                  if (step.unusedKind) onDisableKind(step.unusedKind);
                }}
                className="text-wptext-muted hover:underline"
              >
                この回では使わない
              </button>
            )}
          </li>
        ))}
      </ol>

      <button
        type="button"
        disabled={!hasTarget || publishing}
        onClick={onPublish}
        className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {publishing ? '公開中…' : '公開する'}
      </button>
      {!hasTarget && (
        <span className="ml-2 text-wptext-muted">対象者を決めると公開できます。</span>
      )}
    </section>
  );
}
